/**
 * API utilities for image and video generation
 */

import { getApiUrl } from '../../../config/api';
import { handleAPIError } from '../../../services/apiErrorHandler';
import { 
  GenerateImageRequest, 
  GenerateImageResponse 
} from '../../../services/imageGenerationApi';

// Video generation types
export interface GenerateVideoRequest {
  projectId?: string;
  provider?: 'runway' | 'ltx' | 'veo';
  imageBytes?: string;
  image_url?: string;
  prompt: string;
  duration?: number;
  aspect_ratio?: string;
  quality?: string;
  
  // Runway-specific
  advanced_runway?: {
    motion_types?: string[];
    motion_intensity?: string;
    motion_speed?: number;
    motion_template?: string;
  };
  
  // Veo-specific parameters
  veo_model?: string;
  negative_prompt?: string;
  person_generation?: string;
  dialogue_cue?: string;
  advanced_veo?: {
    visual_style?: string;
    lighting?: string;
    camera_movement?: string;
    sound_effects?: string;
    ambient_noise?: string;
    motion_intensity?: string;
    mood?: string;
    audio_style?: string;
  };
}

export interface GenerateVideoResponse {
  success: boolean;
  message: string;
  data: {
    projectId: string;
    s3Url: string;
    seed: number;
    duration: number;
    prompt: string;
  };
}

/**
 * Generate images using batch endpoint
 */
export const generateImage = async (
  request: GenerateImageRequest
): Promise<GenerateImageResponse> => {
  const response = await fetch(getApiUrl('/image-generation/batch'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(localStorage.getItem('authToken') && {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      })
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    await handleAPIError(response);
  }

  return await response.json();
};

/**
 * Generate video using selected provider (Runway, LTX, or Veo)
 */
export const generateVideo = async (
  request: GenerateVideoRequest
): Promise<GenerateVideoResponse> => {
  console.log('🎬 VIDEO GENERATION API CALL PARAMS:', {
    provider: request.provider || 'runway',
    image_url: request.image_url ? 'Present' : 'Missing',
    prompt: request.prompt,
    duration: request.duration,
    aspect_ratio: request.aspect_ratio,
    quality: request.quality,
    advanced_runway: request.advanced_runway
  });

  const response = await fetch(getApiUrl('/video-generation/single'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(localStorage.getItem('authToken') && {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      })
    },
    body: JSON.stringify({
      ...request,
      provider: request.provider || 'runway'
    }),
  });

  if (!response.ok) {
    await handleAPIError(response);
  }

  const result = await response.json();
  console.log('📥 VIDEO GENERATION API RESPONSE:', result);
  return result;
};
